import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import type { DpEmployeeDetail } from "../api/employeesApi";
import {
  DpLeaveListItem,
  LEAVE_STATUS_BADGE,
  LEAVE_STATUS_LABELS,
  LEAVE_TYPE_LABELS,
  listLeavesApi,
} from "../api/leavesApi";
import { DP_LEAVE_PAGE } from "../routes";
import { formatDateOnly } from "../utils/format";
import LeaveFormModal from "./LeaveFormModal";
import LeavePayerBadge from "./LeavePayerBadge";

type Props = {
  token: string;
  employee: DpEmployeeDetail;
  isDp: boolean;
  /** Lançar afastamento muda o status do colaborador — a ficha recarrega o cabeçalho. */
  onChanged: () => void;
};

/** Aba "Afastamentos" da ficha. Sem dado de saúde: o detalhe (e o DP) é que o mostram. */
export default function EmployeeLeavesTab({ token, employee, isDp, onChanged }: Props) {
  const navigate = useNavigate();
  const [items, setItems] = useState<DpLeaveListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await listLeavesApi(token, { employeeId: employee.id, limit: 100 });
      setItems(result.items);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar os afastamentos.");
    } finally {
      setLoading(false);
    }
  }, [token, employee.id]);

  useEffect(() => {
    load();
  }, [load]);

  const terminated = employee.status === "DESLIGADO";

  return (
    <section className="dpSection">
      <div className="dpHeader" style={{ marginBottom: 12 }}>
        <h2 className="dpSection__title" style={{ margin: 0 }}>
          Afastamentos
        </h2>
        <div className="dpHeader__actions">
          <button
            className="dpBtn dpBtn--primary"
            onClick={() => setCreating(true)}
            disabled={terminated || employee.contracts.length === 0}
            title={terminated ? "Colaborador desligado" : employee.contracts.length === 0 ? "Sem contrato" : undefined}
          >
            Novo afastamento
          </button>
        </div>
      </div>

      <div className="dpTableWrapper">
        <table className="dpTable">
          <thead>
            <tr>
              <th>Tipo</th>
              <th>Período</th>
              <th>Dias</th>
              <th>Quem paga</th>
              <th>Situação</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="dpEmpty">
                  Carregando...
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={5} className="dpEmpty">
                  Nenhum afastamento lançado.
                </td>
              </tr>
            ) : (
              items.map((l) => (
                <tr
                  key={l.id}
                  style={{ cursor: "pointer", ...(l.status === "CANCELADO" ? { opacity: 0.6 } : {}) }}
                  onClick={() => navigate(DP_LEAVE_PAGE(l.id))}
                  title="Abrir o afastamento"
                >
                  <td>
                    <strong>{LEAVE_TYPE_LABELS[l.type]}</strong>
                    {l.parentLeaveId ? <div className="dpMuted">continuação de afastamento anterior</div> : null}
                  </td>
                  <td>
                    {formatDateOnly(l.startDate)} a {l.eventEndDate ? formatDateOnly(l.eventEndDate) : "em aberto"}
                  </td>
                  <td>{l.eventDays ?? "—"}</td>
                  <td>
                    <div className="dpTable__actions">
                      <LeavePayerBadge payer={l.payer} />
                      {l.continuations.length > 0 && l.payer !== "INSS" ? <LeavePayerBadge payer="INSS" /> : null}
                    </div>
                    {l.inssDays ? (
                      <div className="dpMuted">
                        {l.employerPaidDays} da empresa · {l.inssDays} do INSS
                      </div>
                    ) : null}
                  </td>
                  <td>
                    <span className={`dpBadge ${LEAVE_STATUS_BADGE[l.status]}`}>{LEAVE_STATUS_LABELS[l.status]}</span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {creating ? (
        <LeaveFormModal
          token={token}
          employeeId={employee.id}
          isDp={isDp}
          previousLeaves={items}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            load();
            onChanged();
          }}
        />
      ) : null}
    </section>
  );
}
